import { FC } from 'react';
import {
  buildReleaseVersion,
  DeployVersionStatus,
  ReleaseAndDeployments,
} from '@agilelab/plugin-wb-builder-common';
import { useRouteRef } from '@mainstage/core-plugin-api';
import { createSearchParams } from 'react-router-dom';
import { releaseDetailRouteRef } from '../../../routes';
import { DeploymentStatusBadge } from '../../common';
import { snakeCaseToTitleCase } from '@agilelab/plugin-wb-platform-common';
import { WbComponentsListArray } from '@agilelab/plugin-wb-platform';

interface Props {
  release: ReleaseAndDeployments;
}

interface DeploymentChipProps {
  deployment: DeployVersionStatus;
  releaseName: string;
  isDifferentDeployedVersion: boolean;
}

const DeploymentChip: FC<DeploymentChipProps> = ({
  deployment: d,
  releaseName,
  isDifferentDeployedVersion,
}) => {
  const releaseDetailRoute = useRouteRef(releaseDetailRouteRef);
  const tooltipTitle = `${d.status}. Open detail page for ${d.version}`;

  const namespace = 'default';
  const name = releaseName;
  const kind = 'Release';

  const to =
    isDifferentDeployedVersion && d.version
      ? `${releaseDetailRoute({ kind, namespace, name })}?${createSearchParams({
          env: d.environment,
          version: buildReleaseVersion({ v: d.version }) ?? '',
        }).toString()}`
      : undefined;

  switch (d.status) {
    case 'DEPLOYED':
    case 'PARTIALLY_DEPLOYED':
    case 'PROVISIONING_IN_PROGRESS':
    case 'CORRUPT':
      return (
        <DeploymentStatusBadge
          status={d.status}
          tooltipTitle={tooltipTitle}
          to={to}
          showIcon
          showVersion
          version={d.version}
          label={snakeCaseToTitleCase(d.environment)}
        />
      );
    case 'NOT_DEPLOYED':
      return <></>;
    default:
      return <></>;
  }
};

export const ReleaseDeploymentStatus: FC<Props> = ({ release }) => {
  return (
    <WbComponentsListArray>
      {release?.deployments
        ?.filter(d => d.status !== 'NOT_DEPLOYED')
        .map((deployment, index) => {
          const version = deployment.version;
          const isDifferentDeployedVersion =
            version !== release.metadata.version;
          return (
            <DeploymentChip
              key={index}
              deployment={{ ...deployment, version }}
              releaseName={release.metadata.name}
              isDifferentDeployedVersion={isDifferentDeployedVersion}
            />
          );
        })}
    </WbComponentsListArray>
  );
};
